import { useEffect, useRef, useState } from "react";
import { DEFAULT_CUSTOM_STORE } from "@bloom/shared";
import type {
  CustomStoreDesign,
  CustomStoreState,
  CustomStoreProduct,
  DtpCategoryContentResponse,
} from "@bloom/shared";
import api from "../../../lib/api";
import { CustomStoreView } from "./CustomStoreView";
import { CustomProductPagesEditor } from "./CustomProductPagesEditor";
import { CustomProductPageView } from "./CustomProductPageView";
import { resolveStorefrontTheme } from "../../../lib/storefrontTheme";
import { patientHomePath } from "../../../lib/clinicSite";

const base = "/users/me/custom-store";
const inputClass = "w-full border border-gray-300 rounded-lg p-2 text-sm";
const buttonClass =
  "px-3 py-2 rounded-lg border border-gray-300 text-sm disabled:opacity-50";
const errorText = (e: any) =>
  e?.response?.data?.error?.message ||
  e?.response?.data?.error ||
  e?.message ||
  "Could not save. Please retry.";
export function CustomStoreEditor({
  clinicName,
  clinicId,
  onPublished,
  educationalStartRequest = 0,
  templateHeadline,
  templateIntroduction,
  liveVariant,
}: {
  clinicName: string;
  clinicId: string;
  onPublished: () => Promise<void>;
  educationalStartRequest?: number;
  templateHeadline?: string;
  templateIntroduction?: string;
  liveVariant?: string;
}) {
  const [state, setState] = useState<CustomStoreState>();
  const [design, setDesign] = useState<CustomStoreDesign>(
    structuredClone(DEFAULT_CUSTOM_STORE),
  );
  const [categoryContent, setCategoryContent] = useState<
    DtpCategoryContentResponse[]
  >([]);
  const [confirmEducational, setConfirmEducational] = useState(false);
  useEffect(() => {
    if (educationalStartRequest) setConfirmEducational(true);
  }, [educationalStartRequest]);
  const [products, setProducts] = useState<CustomStoreProduct[]>([]);
  const [assets, setAssets] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const previewRef = useRef<HTMLElement>(null);
  const [preview, setPreview] = useState(false);
  const [previewProductId, setPreviewProductId] = useState("");
  const [section, setSection] = useState("home");
  const [mobile, setMobile] = useState(false);
  const [tokens, setTokens] = useState<
    { id: string; name: string; expiresAt: string; scope: string }[]
  >([]);
  const [tokenName, setTokenName] = useState("Store assistant");
  const [tokenScope, setTokenScope] = useState("edit");
  const [newToken, setNewToken] = useState("");
  const dirty =
    !!state && JSON.stringify(design) !== JSON.stringify(state.draft.design);
  const portalTheme = resolveStorefrontTheme({
    customAccent: design.accent,
    customFont: design.font,
  });
  async function refresh() {
    setPreview(false);
    const [s, p, t, c]: any[] = await Promise.all([
      api.get(base),
      api.get(base + "/catalog"),
      api.get(base + "/tokens"),
      api.get(base + "/category-content"),
    ]);
    setState(s.data);
    setDesign(s.data.draft.design);
    setProducts(p.data);
    setTokens(t.data);
    setCategoryContent(c.data);
    const requested = new URLSearchParams(window.location.search).get(
      "customPreviewRevision",
    );
    if (requested !== null) {
      const expectedClinic = new URLSearchParams(window.location.search).get(
        "customPreviewClinic",
      );
      if (expectedClinic && expectedClinic !== clinicId)
        setError(
          "This preview belongs to another clinic. Sign in with that clinic account.",
        );
      else if (String(s.data.draft.revision) === requested) setPreview(true);
      else
        setError(
          "This preview is for an older draft. Review the current saved draft instead.",
        );
    }
  }
  useEffect(() => {
    refresh().catch((e) => setError(errorText(e)));
  }, []);
  const ids = [
    design.logoAssetId,
    design.faviconAssetId,
    design.hero.imageAssetId,
  ]
    .filter(Boolean)
    .join(",");
  useEffect(() => {
    let active = true;
    const urls: string[] = [];
    Promise.all(
      ids
        .split(",")
        .filter(Boolean)
        .map(async (id) => {
          const blob = await api.customStoreAssetBlob(id);
          const url = URL.createObjectURL(blob);
          urls.push(url);
          return [id, url];
        }),
    )
      .then((entries) => {
        if (active) setAssets(Object.fromEntries(entries));
        else urls.forEach(URL.revokeObjectURL);
      })
      .catch(() => {
        if (active)
          setError("Could not load a draft image. Reload before publishing.");
      });
    return () => {
      active = false;
      urls.forEach(URL.revokeObjectURL);
    };
  }, [ids]);
  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await work();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const patch = (change: Partial<CustomStoreDesign>) => {
    setDesign((d) => ({ ...d, ...change }));
    setPreview(false);
  };
  async function save() {
    const res: any = await api.put(base + "/draft", {
      expectedRevision: state!.draft.revision,
      design,
    });
    setState(res.data);
    setDesign(res.data.draft.design);
    setNotice("Draft saved. Your live store has not changed.");
  }
  async function upload(
    file: File,
    slot: "logoAssetId" | "faviconAssetId" | "hero",
  ) {
    await run(async () => {
      const res = await api.uploadCustomStoreAsset(file);
      patch(
        slot === "hero"
          ? { hero: { ...design.hero, imageAssetId: res.data.id } }
          : { [slot]: res.data.id },
      );
    });
  }
  if (!state)
    return (
      <div className="mt-6 p-6 border rounded-xl">
        {error || "Loading custom store…"}
        {error && (
          <button className={buttonClass} onClick={() => run(refresh)}>
            Retry
          </button>
        )}
      </div>
    );
  return (
    <section className="bg-white border border-gray-200 rounded-xl">
      <div className="sticky top-0 z-10 rounded-t-xl border-b border-gray-200 bg-white p-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            Your custom store
          </h2>
          <p className="text-xs text-gray-500 mt-1">
            Draft {state.draft.revision} ·{" "}
            {dirty ? "Unsaved changes" : "All changes saved"} ·{" "}
            {state.published
              ? `Published version ${state.published.revision}`
              : "Not published"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            disabled={busy || !dirty}
            className={buttonClass}
            onClick={() => run(save)}
          >
            Save draft
          </button>
          <button
            disabled={busy || dirty}
            className={buttonClass}
            onClick={() => {
              setPreview(true);
              previewRef.current?.scrollIntoView?.({ block: "start" });
            }}
          >
            Review saved draft
          </button>
          <button
            disabled={busy || dirty || !preview}
            className="px-4 py-2 rounded-lg bg-blue-700 text-white disabled:opacity-50"
            onClick={() =>
              run(async () => {
                const res: any = await api.post(base + "/publish", {
                  expectedRevision: state.draft.revision,
                });
                setState(res.data);
                await onPublished();
                setPreview(false);
                setNotice("Published. Your Custom store is now live.");
              })
            }
          >
            Publish this version
          </button>
        </div>
      </div>
      <div className="border-b border-gray-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {liveVariant === "custom"
            ? "Your custom store is live."
            : "Your live store uses a Bloom template. Publishing this draft switches to your custom store."}{" "}
          Starting point:{" "}
          <span className="capitalize font-medium">{design.template}</span>.
        </p>
        <button
          className={buttonClass}
          disabled={busy}
          onClick={() => setConfirmEducational(true)}
        >
          Start from Educational
        </button>
      </div>
      {confirmEducational && (
        <div className="m-4 rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-3">
          <h3 className="font-semibold">
            Use Educational as your starting point?
          </h3>
          <p className="text-sm text-gray-700">
            This updates your draft's homepage introduction, fonts, and colors
            and adds category browsing. Your images, product pages, featured
            selections, about text, and FAQs are kept. Your live store stays as
            it is until you publish.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              disabled={busy}
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm text-white disabled:opacity-50"
              onClick={() => {
                patch({
                  template: "educational",
                  accent: "green",
                  font: "serif",
                  hero: {
                    ...design.hero,
                    heading:
                      templateHeadline || "Medicine, prescribed for you.",
                    body:
                      templateIntroduction ||
                      `Each medication in this store has been selected by ${clinicName} for our patients. Read about what each one does, how it works, and whether it's right for you — then request a consult.`,
                  },
                  sections: design.sections.includes("categories")
                    ? design.sections
                    : [
                        "hero",
                        "categories",
                        ...design.sections.filter((s) => s !== "hero"),
                      ],
                });
                setConfirmEducational(false);
                setSection("home");
                setPreviewProductId("");
                setNotice(
                  "Educational starting point applied to your draft. Save and review before publishing.",
                );
              }}
            >
              Use Educational starting point
            </button>
            <button
              className={buttonClass}
              onClick={() => setConfirmEducational(false)}
            >
              Keep current draft
            </button>
          </div>
        </div>
      )}
      {error && (
        <p
          role="alert"
          className="m-4 rounded-lg bg-red-50 p-3 text-sm text-red-700"
        >
          {error}
        </p>
      )}
      {notice && (
        <p
          role="status"
          className="m-4 rounded-lg bg-green-50 p-3 text-sm text-green-800"
        >
          {notice}
        </p>
      )}
      <nav
        aria-label="Store editor sections"
        className="flex flex-wrap gap-1 border-b border-gray-200 px-4 py-3"
      >
        {[
          ["home", "Home page"],
          ["products", "Product pages"],
          ["branding", "Branding"],
          ["manage", "Versions & connections"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            aria-pressed={section === id}
            onClick={() => {
              setSection(id);
              if (id === "home") setPreviewProductId("");
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${section === id ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}
          >
            {label}
          </button>
        ))}
      </nav>
      <div className="grid min-w-0 lg:grid-cols-[minmax(320px,2fr)_minmax(0,3fr)]">
        <div className="min-w-0 p-5 lg:p-6">
          <fieldset disabled={busy} className="min-w-0">
            <div hidden={section !== "branding"} className="space-y-5">
              <div>
                <h3 className="font-semibold">
                  Store &amp; patient portal branding
                </h3>
                <p className="mt-1 text-sm text-gray-600">
                  Your logo, browser icon, accent, and fonts carry through to
                  checkout and the patient portal. Review and publish your draft
                  to make them live.
                </p>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {(["template", "accent", "font"] as const).map((key) => (
                  <label key={key} className="text-sm capitalize">
                    {key}
                    <select
                      className={inputClass}
                      value={design[key]}
                      onChange={(e) => {
                        if (key === "template") {
                          if (e.target.value === "educational")
                            setConfirmEducational(true);
                          else
                            patch({
                              template: e.target.value as
                                "studio" | "editorial",
                              sections: design.sections.filter(
                                (s) => s !== "categories",
                              ),
                            });
                        } else patch({ [key]: e.target.value });
                      }}
                    >
                      {{
                        template: ["studio", "editorial", "educational"],
                        accent: ["blue", "green", "plum"],
                        font: ["sans", "serif"],
                      }[key].map((v) => (
                        <option key={v}>{v}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <div className="grid gap-4">
                {(["logoAssetId", "faviconAssetId", "hero"] as const).map(
                  (slot) => {
                    const id =
                      slot === "hero" ? design.hero.imageAssetId : design[slot];
                    return (
                      <div key={slot}>
                        <label className="text-sm block">
                          {slot === "hero"
                            ? "Hero image"
                            : slot === "logoAssetId"
                              ? "Logo"
                              : "Browser icon"}
                          <input
                            className="mt-2 block w-full text-xs"
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) upload(file, slot);
                              e.target.value = "";
                            }}
                          />
                        </label>
                        {id && (
                          <>
                            <img
                              src={assets[id]}
                              alt="Draft upload"
                              className="h-20 max-w-full object-contain my-2"
                            />
                            <button
                              type="button"
                              className={buttonClass}
                              onClick={() =>
                                patch(
                                  slot === "hero"
                                    ? {
                                        hero: {
                                          ...design.hero,
                                          imageAssetId: null,
                                        },
                                      }
                                    : { [slot]: null },
                                )
                              }
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
              <p className="text-xs text-gray-500">
                PNG, JPEG or WebP, up to 2 MB. Uploads are private until
                published.
              </p>
              <section
                aria-label="Patient portal branding preview"
                className="rounded-xl border overflow-hidden"
                style={{
                  borderColor: portalTheme.border,
                  background: portalTheme.bg,
                  color: portalTheme.text,
                  fontFamily: portalTheme.bodyFamily,
                }}
              >
                <div
                  className="px-5 py-4 border-b flex items-center gap-3"
                  style={{ borderColor: portalTheme.border }}
                >
                  {design.logoAssetId && assets[design.logoAssetId] && (
                    <img
                      src={assets[design.logoAssetId]}
                      alt="Portal logo preview"
                      className="h-8 max-w-[120px] object-contain"
                    />
                  )}
                  <span className="font-semibold">{clinicName}</span>
                </div>
                <div className="p-5 space-y-3">
                  <p className="text-xs" style={{ color: portalTheme.muted }}>
                    Patient portal · Branding preview
                  </p>
                  <h4
                    className="text-2xl"
                    style={{
                      fontFamily: portalTheme.headingFamily,
                      color: portalTheme.text,
                    }}
                  >
                    Your care, in one place
                  </h4>
                  <p className="text-sm" style={{ color: portalTheme.muted }}>
                    Orders, results, membership, and account details.
                  </p>
                  <span
                    className="inline-block rounded-lg px-4 py-2 text-sm"
                    style={{
                      background: portalTheme.accent,
                      color: portalTheme.accentForeground,
                    }}
                  >
                    View your orders
                  </span>
                </div>
              </section>
              <p className="text-xs text-gray-500">
                This preview uses your current draft. Customization uses the
                controls above; custom CSS, HTML, and JavaScript are not
                supported.
              </p>
              {state.published && (
                <a
                  href={patientHomePath(clinicId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-[44px] items-center text-sm text-blue-700 underline"
                >
                  Open patient portal
                </a>
              )}
            </div>
            <div hidden={section !== "home"} className="space-y-5">
              <div>
                <h3 className="font-semibold text-gray-900">Home page</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Introduce your clinic and help patients find a treatment.
                </p>
              </div>
              <label className="block text-sm">
                Headline
                <input
                  maxLength={120}
                  className={inputClass}
                  value={design.hero.heading}
                  onChange={(e) =>
                    patch({ hero: { ...design.hero, heading: e.target.value } })
                  }
                />
              </label>
              <label className="block text-sm">
                Introduction
                <textarea
                  maxLength={500}
                  className={inputClass}
                  value={design.hero.body}
                  onChange={(e) =>
                    patch({ hero: { ...design.hero, body: e.target.value } })
                  }
                />
              </label>
              <div>
                <h3 className="font-medium mb-2">Featured treatments</h3>
                {design.featuredProductIds.some(
                  (id) => !products.some((p) => p._id === id),
                ) && (
                  <div className="text-sm text-amber-900 mb-3">
                    <p>
                      A selected treatment is no longer available. Remove
                      unavailable selections before publishing.
                    </p>
                    <button
                      type="button"
                      className={buttonClass}
                      onClick={() =>
                        patch({
                          featuredProductIds: design.featuredProductIds.filter(
                            (id) => products.some((p) => p._id === id),
                          ),
                        })
                      }
                    >
                      Remove unavailable selections
                    </button>
                  </div>
                )}
                <div className="max-h-52 overflow-auto space-y-2">
                  {products.map((p) => (
                    <label key={p._id} className="flex gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={design.featuredProductIds.includes(p._id)}
                        disabled={
                          !design.featuredProductIds.includes(p._id) &&
                          design.featuredProductIds.length >= 12
                        }
                        onChange={(e) =>
                          patch({
                            featuredProductIds: e.target.checked
                              ? [...design.featuredProductIds, p._id]
                              : design.featuredProductIds.filter(
                                  (id) => id !== p._id,
                                ),
                          })
                        }
                      />
                      {p.displayName}
                    </label>
                  ))}
                </div>
              </div>
              <label className="block text-sm">
                About heading
                <input
                  maxLength={100}
                  className={inputClass}
                  value={design.about.heading}
                  onChange={(e) =>
                    patch({
                      about: { ...design.about, heading: e.target.value },
                    })
                  }
                />
              </label>
              <label className="block text-sm">
                About your clinic
                <textarea
                  maxLength={2000}
                  rows={4}
                  className={inputClass}
                  value={design.about.body}
                  onChange={(e) =>
                    patch({ about: { ...design.about, body: e.target.value } })
                  }
                />
              </label>
              <div>
                <h3 className="font-medium">FAQs</h3>
                {design.faqs.map((faq, i) => (
                  <div key={i} className="space-y-2 mt-3">
                    <label className="block text-sm">
                      Question {i + 1}
                      <input
                        className={inputClass}
                        maxLength={160}
                        value={faq.question}
                        onChange={(e) =>
                          patch({
                            faqs: design.faqs.map((f, j) =>
                              j === i ? { ...f, question: e.target.value } : f,
                            ),
                          })
                        }
                      />
                    </label>
                    <label className="block text-sm">
                      Answer
                      <textarea
                        className={inputClass}
                        maxLength={1000}
                        value={faq.answer}
                        onChange={(e) =>
                          patch({
                            faqs: design.faqs.map((f, j) =>
                              j === i ? { ...f, answer: e.target.value } : f,
                            ),
                          })
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className={buttonClass}
                      onClick={() =>
                        patch({ faqs: design.faqs.filter((_, j) => i !== j) })
                      }
                    >
                      Remove question
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className={`${buttonClass} mt-3`}
                  disabled={design.faqs.length >= 12}
                  onClick={() =>
                    patch({
                      faqs: [...design.faqs, { question: "", answer: "" }],
                    })
                  }
                >
                  Add question
                </button>
              </div>
              <div>
                <h3 className="font-medium">Sections</h3>
                {design.sections.map((section, i) => (
                  <div key={section} className="flex gap-2 items-center py-2">
                    <span className="flex-1 capitalize">{section}</span>
                    <button
                      type="button"
                      className={buttonClass}
                      disabled={i === 0}
                      onClick={() => {
                        const a = [...design.sections];
                        [a[i - 1], a[i]] = [a[i], a[i - 1]];
                        patch({ sections: a });
                      }}
                    >
                      Move up
                    </button>
                    {section !== "hero" && (
                      <button
                        type="button"
                        className={buttonClass}
                        onClick={() =>
                          patch({
                            sections: design.sections.filter(
                              (s) => s !== section,
                            ),
                          })
                        }
                      >
                        Hide
                      </button>
                    )}
                  </div>
                ))}
                {(["categories", "featured", "about", "faq"] as const)
                  .filter(
                    (s) =>
                      !design.sections.includes(s) &&
                      (s !== "categories" || design.template === "educational"),
                  )
                  .map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={`${buttonClass} mr-2`}
                      onClick={() =>
                        patch({ sections: [...design.sections, s] })
                      }
                    >
                      Show {s}
                    </button>
                  ))}
              </div>
            </div>
            <div hidden={section !== "products"}>
              <CustomProductPagesEditor
                onSelect={setPreviewProductId}
                pages={design.productPages || []}
                products={products}
                onChange={(productPages) => patch({ productPages })}
              />
            </div>
          </fieldset>
          <div hidden={section !== "manage"} className="space-y-5">
            <div>
              <h3 className="font-semibold">Versions & connections</h3>
              <p className="text-sm text-gray-500 mt-1">
                Load the latest saved draft, restore a version, or connect a
                store assistant.
              </p>
            </div>
            <button
              disabled={busy || dirty}
              className={buttonClass}
              onClick={() => run(refresh)}
            >
              Reload latest draft
            </button>

            {state.previous && (
              <div className="border-t pt-4">
                <button
                  className={buttonClass}
                  disabled={busy || dirty}
                  onClick={() =>
                    run(async () => {
                      const res: any = await api.post(base + "/restore", {
                        expectedRevision: state.draft.revision,
                        publishedRevision: state.published!.revision,
                      });
                      setState(res.data);
                      setDesign(res.data.draft.design);
                      setPreview(false);
                      setNotice(
                        "Previous published version restored to your draft. Review and publish when ready.",
                      );
                    })
                  }
                >
                  Restore previous version to draft
                </button>
              </div>
            )}
            <details className="border-t pt-4">
              <summary className="cursor-pointer font-medium">
                Connect a store assistant (MCP)
              </summary>
              <p className="text-sm text-gray-600 my-3">
                Connections can read your store or edit drafts. Only you can
                publish in Bloom. Keys expire after 30 days.
              </p>
              <label className="text-sm block">
                Connection name
                <input
                  className={inputClass}
                  value={tokenName}
                  maxLength={80}
                  onChange={(e) => setTokenName(e.target.value)}
                />
              </label>
              <select
                aria-label="Connection access"
                className={`${inputClass} mt-2`}
                value={tokenScope}
                onChange={(e) => setTokenScope(e.target.value)}
              >
                <option value="edit">Read and edit drafts</option>
                <option value="read">Read only</option>
              </select>
              <button
                disabled={busy}
                className={`${buttonClass} my-3`}
                onClick={() =>
                  run(async () => {
                    const res: any = await api.post(base + "/tokens", {
                      name: tokenName,
                      scope: tokenScope,
                    });
                    setNewToken(res.data.token);
                    const list: any = await api.get(base + "/tokens");
                    setTokens(list.data);
                  })
                }
              >
                Create connection key
              </button>
              {newToken && (
                <div className="bg-blue-50 p-3 rounded-lg text-sm">
                  <p>Copy this key now. It will not be shown again.</p>
                  <input
                    aria-label="New connection key"
                    readOnly
                    value={newToken}
                    className={`${inputClass} my-2 font-mono`}
                  />
                  <button
                    className={buttonClass}
                    onClick={() => setNewToken("")}
                  >
                    Hide key
                  </button>
                </div>
              )}
              <p className="text-sm my-3 break-all">
                Endpoint: {window.location.origin}/api/store-mcp
              </p>
              <p className="text-xs text-gray-600 mb-3">
                Use a client that supports a custom Authorization header: Bearer
                followed by your key.
              </p>
              {tokens.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center justify-between gap-3 py-2 text-sm"
                >
                  <span>
                    {t.name} · {t.scope} · expires{" "}
                    {new Date(t.expiresAt).toLocaleDateString()}
                  </span>
                  <button
                    disabled={busy}
                    className={buttonClass}
                    onClick={() =>
                      run(async () => {
                        await api.delete(base + "/tokens/" + t.id);
                        setTokens(tokens.filter((x) => x.id !== t.id));
                        setNewToken("");
                      })
                    }
                  >
                    Revoke
                  </button>
                </div>
              ))}
            </details>
          </div>
        </div>
        <aside
          ref={previewRef}
          aria-label="Store preview"
          className="min-w-0 scroll-mt-48 bg-gray-50 p-4 lg:p-6"
        >
          <div className="lg:sticky lg:top-6">
            <div className="mb-4">
              <h3 className="font-semibold text-gray-900">
                {preview ? "Saved draft review" : "Live draft preview"}
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                {preview
                  ? "Review this saved version before publishing."
                  : "Changes appear here as you edit. Save and review to publish."}
              </p>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              <button
                aria-pressed={!mobile}
                className={buttonClass}
                onClick={() => setMobile(false)}
              >
                Desktop preview
              </button>
              <button
                aria-pressed={mobile}
                className={buttonClass}
                onClick={() => setMobile(true)}
              >
                Mobile preview
              </button>
            </div>
            <div
              className="border border-gray-200 bg-white rounded-xl p-4 mx-auto overflow-auto max-h-[75vh]"
              style={{
                maxWidth: mobile ? 390 : "100%",
                containerType: "inline-size",
              }}
            >
              {design.logoAssetId && assets[design.logoAssetId] ? (
                <img
                  className="h-10 w-40 object-contain object-left"
                  src={assets[design.logoAssetId]}
                  alt={clinicName}
                />
              ) : (
                <strong>{clinicName}</strong>
              )}
              <label className="block text-sm my-4">
                Preview page
                <select
                  className={inputClass}
                  value={previewProductId}
                  onChange={(e) => setPreviewProductId(e.target.value)}
                >
                  <option value="">Storefront</option>
                  {(design.productPages || [])
                    .filter((page) =>
                      products.some((p) => p._id === page.productId),
                    )
                    .map((page) => (
                      <option key={page.productId} value={page.productId}>
                        {page.heading}
                      </option>
                    ))}
                </select>
              </label>
              {(() => {
                const page = design.productPages?.find(
                  (p) => p.productId === previewProductId,
                );
                const product = products.find(
                  (p) => p._id === previewProductId,
                );
                return page && product ? (
                  <CustomProductPageView
                    design={design}
                    page={page}
                    product={product}
                    preview
                  />
                ) : (
                  <CustomStoreView
                    design={design}
                    categoryContent={categoryContent}
                    clinicName={clinicName}
                    products={products}
                    assetUrl={(id) => assets[id]}
                    onProduct={(id) => {
                      if (design.productPages?.some((p) => p.productId === id))
                        setPreviewProductId(id);
                    }}
                  />
                );
              })()}
              <div
                id="clinic-products"
                className="border-t pt-4 text-sm text-gray-600"
              >
                {design.template === "educational"
                  ? "Category browsing and treatment explainers open in your published store. Checkout stays in Bloom."
                  : "Your full catalog and Bloom checkout appear below these sections."}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
